<template>
  <div class="media-page">
    <!-- 顶部工具条 -->
    <div class="card toolbar">
      <div class="toolbar-left">
        <span class="toolbar-title">当前单词：</span>
        <span class="toolbar-word">{{ word || '未选择' }}</span>
      </div>
      <div class="toolbar-right">
        <el-button size="small" icon="el-icon-refresh" @click="refresh">刷新</el-button>
        <el-button size="small" type="primary" icon="el-icon-upload2" @click="openUpload">
          上传{{ type === 'image' ? '图片' : '视频' }}
        </el-button>
      </div>
    </div>

    <!-- 媒体展示 -->
    <div class="card media-card" v-if="list.length">
      <div class="media-stage">
        <el-button class="nav-arrow prev" icon="el-icon-arrow-left" circle :disabled="currentIndex === 0" @click="goPrev" />

        <div class="media-body">
          <img v-if="type === 'image'" :src="currentMediaUrl" class="media-img" alt="图片" />
          <video v-else :src="currentMediaUrl" class="media-video" controls />
        </div>

        <el-button class="nav-arrow next" icon="el-icon-arrow-right" circle :disabled="currentIndex === list.length - 1" @click="goNext" />
      </div>

      <!-- 信息 + 操作 -->
      <div class="media-meta">
        <div class="meta-top">
          <span class="index-text">{{ currentIndex + 1 }} / {{ list.length }}</span>
          <span class="creator" v-if="currentItem.creatorName">上传：{{ currentItem.creatorName }}</span>
        </div>
        <div class="tags-row" v-if="currentItem.tagsArr.length">
          <el-tag v-for="(t, i) in currentItem.tagsArr" :key="i" size="small" class="tag">{{ t }}</el-tag>
        </div>
        <p class="desc" v-if="currentItem.description">{{ currentItem.description }}</p>
        <div class="actions">
          <el-button size="small" :type="currentItem.isLike ? 'danger' : 'default'" icon="el-icon-thumb" @click="toggleLike">
            点赞 {{ currentItem.likeCount || 0 }}
          </el-button>
          <el-button size="small" :type="currentItem.isCollect ? 'warning' : 'default'" icon="el-icon-star-off" @click="toggleCollect">
            收藏 {{ currentItem.collectCount || 0 }}
          </el-button>
          <el-button size="small" icon="el-icon-chat-dot-round" @click="openComment">
            评论 {{ currentItem.commentCount || 0 }}
          </el-button>
        </div>
      </div>

      <!-- 缩略图条 -->
      <div class="thumb-strip">
        <div
          v-for="(item, i) in list"
          :key="item.id"
          class="thumb"
          :class="{ active: i === currentIndex }"
          @click="currentIndex = i"
        >
          <img v-if="type === 'image'" :src="item.imgUrl" alt="缩略图" />
          <video v-else :src="item.videoUrl" muted />
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div class="card empty-state" v-else v-loading="loading">
      <el-empty :description="emptyText" />
    </div>

    <!-- 上传弹窗 -->
    <el-dialog :title="'上传' + (type === 'image' ? '图片' : '视频')" :visible.sync="showUpload" width="480px">
      <el-form label-width="70px">
        <el-form-item label="文件">
          <input type="file" :accept="accept" @change="onFileChange" />
        </el-form-item>
        <el-form-item label="单词">
          <el-input v-model="uploadForm.word" placeholder="请输入单词" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="uploadForm.description" type="textarea" :rows="2" placeholder="描述（可选）" />
        </el-form-item>
        <el-form-item label="标签">
          <el-input v-model="uploadForm.tags" placeholder="多个标签用逗号分隔（可选）" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showUpload = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="submitUpload">上传</el-button>
      </span>
    </el-dialog>

    <!-- 评论弹窗 -->
    <el-dialog title="评论" :visible.sync="showComment" width="520px">
      <div class="comment-list">
        <el-empty v-if="!commentList.length" description="暂无评论" :image-size="60" />
        <div v-for="c in commentList" :key="c.cid" class="comment-item">
          <span class="comment-name">{{ c.name }}</span>
          <span class="comment-content">{{ c.content }}</span>
        </div>
      </div>
      <div class="comment-input">
        <el-input v-model="commentText" placeholder="写下你的评论" @keyup.enter.native="submitComment" />
        <el-button type="primary" style="margin-top: 8px" @click="submitComment">发表</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '../../utils/request'

export default {
  name: 'MediaWord',
  props: {
    type: {
      type: String,
      default: 'image',
      validator: (v) => ['image', 'video'].includes(v)
    }
  },
  data() {
    return {
      list: [],
      currentIndex: 0,
      loading: false,
      currentQueryWord: '',
      showUpload: false,
      uploading: false,
      uploadForm: {
        file: null,
        word: '',
        description: '',
        tags: ''
      },
      showComment: false,
      commentList: [],
      commentText: '',
      currentId: ''
    }
  },
  computed: {
    word() {
      return this.$store.state.word
    },
    isLogin() {
      return this.$store.getters.isLogin
    },
    accept() {
      return this.type === 'image' ? 'image/*' : 'video/*'
    },
    currentItem() {
      return this.list[this.currentIndex] || {}
    },
    currentMediaUrl() {
      return this.type === 'image' ? this.currentItem.imgUrl : this.currentItem.videoUrl
    },
    emptyText() {
      return this.word ? `暂无「${this.word}」的${this.type === 'image' ? '图片' : '视频'}` : '请先搜索单词'
    },
    queryEndpoint() {
      return this.type === 'image' ? '/image/word' : '/video/word'
    },
    uploadEndpoint() {
      return this.type === 'image' ? '/image/upload' : '/video/upload'
    }
  },
  watch: {
    word(val) {
      if (val && val.trim()) this.fetch(val)
    }
  },
  mounted() {
    const word = (this.$store.state.word || '').trim()
    if (word) this.fetch(word)
  },
  methods: {
    async fetch(word) {
      if (this.loading) return
      this.loading = true
      const kw = word.trim()
      this.currentQueryWord = kw
      try {
        const res = await request.get(this.queryEndpoint, { params: { word: kw } })
        const records = Array.isArray(res.data) ? res.data : []
        records.forEach((item) => {
          item.isLike = false
          item.isCollect = false
          item.tagsArr = (item.tags && item.tags.length > 0)
            ? item.tags.split(',').map((t) => t.trim()).filter((t) => t)
            : []
        })
        this.list = records
        this.currentIndex = 0
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.loading = false
      }
    },

    refresh() {
      this.fetch(this.word)
    },
    goPrev() {
      if (this.currentIndex > 0) this.currentIndex--
    },
    goNext() {
      if (this.currentIndex < this.list.length - 1) this.currentIndex++
    },

    toggleLike() {
      const item = this.currentItem
      if (!item.id) return
      item.isLike = !item.isLike
      item.likeCount = Math.max(0, (item.likeCount || 0) + (item.isLike ? 1 : -1))
      this.$set(this.list, this.currentIndex, { ...item })
    },
    toggleCollect() {
      const item = this.currentItem
      if (!item.id) return
      item.isCollect = !item.isCollect
      item.collectCount = Math.max(0, (item.collectCount || 0) + (item.isCollect ? 1 : -1))
      this.$set(this.list, this.currentIndex, { ...item })
    },

    openComment() {
      if (!this.currentItem.id) return
      this.currentId = this.currentItem.id
      this.commentText = ''
      this.showComment = true
    },
    submitComment() {
      const text = (this.commentText || '').trim()
      if (!text) {
        this.$message.warning('评论内容不能为空')
        return
      }
      const info = this.$store.state.teacherInfo || {}
      const name = info.username || info.name || '游客'
      this.commentList.push({ cid: Date.now(), name, content: text })
      this.currentItem.commentCount = (this.currentItem.commentCount || 0) + 1
      this.$set(this.list, this.currentIndex, { ...this.currentItem })
      this.commentText = ''
      this.$message.success('评论成功')
    },

    openUpload() {
      if (!this.isLogin) {
        this.$message.warning('请先登录')
        this.$router.push('/login')
        return
      }
      this.uploadForm = { file: null, word: this.word || '', description: '', tags: '' }
      this.uploading = false
      this.showUpload = true
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      const maxMB = this.type === 'image' ? 10 : 100
      if (file.size > maxMB * 1024 * 1024) {
        this.$message.warning(`${this.type === 'image' ? '图片' : '视频'}不能超过 ${maxMB}MB`)
        e.target.value = ''
        return
      }
      this.uploadForm.file = file
    },
    async submitUpload() {
      const { file, word, description, tags } = this.uploadForm
      if (!file) {
        this.$message.warning(this.type === 'image' ? '请先选择图片' : '请先选择视频')
        return
      }
      if (!word || !word.trim()) {
        this.$message.warning('请填写单词')
        return
      }
      this.uploading = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        fd.append('word', word.trim())
        fd.append('description', description || '')
        fd.append('tags', tags || '')
        const res = await request.post(this.uploadEndpoint, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (res.code === 200) {
          this.$message.success('上传成功')
          this.showUpload = false
          this.$store.dispatch('setWord', word.trim())
          this.fetch(word.trim())
        }
      } catch (e) {
        // 拦截器已提示
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.toolbar-word {
  font-size: 18px;
  font-weight: 700;
  color: #409eff;
}

.media-card {
  padding: 16px;
}
.media-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.media-body {
  flex: 1;
  display: flex;
  justify-content: center;
  min-height: 320px;
  background: #f7f8fa;
  border-radius: 8px;
  overflow: hidden;
}
.media-img {
  max-width: 100%;
  max-height: 520px;
  object-fit: contain;
}
.media-video {
  width: 100%;
  max-height: 520px;
}
.nav-arrow {
  flex: none;
}

.media-meta {
  margin-top: 14px;
}
.meta-top {
  display: flex;
  align-items: center;
  gap: 16px;
  color: #909399;
  font-size: 13px;
}
.index-text {
  color: #303133;
  font-weight: 600;
}
.tags-row {
  margin-top: 8px;
}
.tag {
  margin-right: 6px;
}
.desc {
  margin-top: 8px;
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
}
.actions {
  margin-top: 12px;
}

.thumb-strip {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  overflow-x: auto;
}
.thumb {
  width: 72px;
  height: 56px;
  flex: none;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
}
.thumb.active {
  border-color: #409eff;
}
.thumb img,
.thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty-state {
  padding: 40px 0;
}

.comment-list {
  max-height: 300px;
  overflow-y: auto;
}
.comment-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.comment-name {
  color: #409eff;
  font-weight: 600;
  margin-right: 10px;
}
.comment-content {
  color: #303133;
}
</style>
